import React, { useEffect, useState, useCallback } from 'react';
import { Col, Card, CardHeader, CardBody, Button, Row } from 'reactstrap';
import {
    Grid,
    GridNoRecords,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowsRotate } from '@fortawesome/pro-light-svg-icons';
import PropTypes from 'prop-types';
import CompleteCountFilterActionCell from './CompleteCountFilterActionCell';
import { DropDownCell } from '../../../Shared/DropDownCell';
import ConfirmDialog from '../../../Shared/ConfirmDialog';
import { useNotification } from '../../../Shared/Notifications/NotificationProvider';
import { NotificationTypes } from '../../../Shared/Notifications/Notification';

const idField = "id";
const editField = "inEdit";

const FieldsDropDown = (props) => (
    <DropDownCell
        {...props}
        dataSourceField="dataSourceFields"
        addEmptyItem={false}
    />
);

const CompleteCountFilters = ({
    filters,
    filterFields,
    onSave,
    onIsAnyChangeChanged,
    onReloadStratums,
    readOnly,
}) => {
    const intl = useIntl();
    const dispatchNotification = useNotification();
    const pageId = 'CompleteCountFilters';

    const [dataState, setDataState] = useState({});
    const [data, setData] = useState([]);
    const [fields, setFields] = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [disable, setDisable] = useState(false);
    const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    const isAnyItemDeleted = filters.length > data.length;
    const isAnyChange = (data.some((x) => x.changed) && data.every((x) => !x.inEdit)) || isAnyItemDeleted;

    const resetData = useCallback(() => {
        const newData = filters.map((filter) => {
            return {
                ...filter,
                changed: false,
            };
        });
        setData(newData);
    }, [filters]);

    useEffect(() => {
        resetData();
    }, [resetData]);

    useEffect(() => {
        const newFields = filterFields.map((field) => {
            return {
                value: field.field,
                text: field.fieldName,
            };
        });
        setFields(newFields);
    }, [filterFields]);

    useEffect(() => {
        if (editItem) {
            setDisable(true);
        }
        else {
            setDisable(false);
        }
    }, [editItem]);

    useEffect(() => {
        onIsAnyChangeChanged(isAnyChange);
    }, [isAnyChange, onIsAnyChangeChanged]);

    const handleReset = () => {
        resetData();
    };

    const handleSave = () => {
        onSave(data);
    };

    const handleAddFilter = () => {
        const newDataItem = {
            id: 0,
            field: undefined,
            fromValue: undefined,
            toValue: undefined,
            inEdit: true,
            changed: false,
        };
        setData([newDataItem, ...data]);
        setEditItem(newDataItem);
    };

    const itemChange = (event) => {
        const newData = data.map((item) =>
            item.id === event.dataItem.id
                ? { ...item, [event.field || ""]: event.value, changed: true }
                : item
        );
        setData(newData);
    };

    const add = (dataItem) => {
        if (!isDataItemValid(dataItem)) {
            return;
        }

        const newDataItem = {
            ...dataItem,
            id: Math.max(...data.map((x) => x.id)) + 1,
            fromValue: dataItem.fromValue !== '' ? dataItem.fromValue : undefined,
            toValue: dataItem.toValue !== '' ? dataItem.toValue : undefined,
            inEdit: false,
        };

        setData([...data.filter((x) => x.id !== dataItem.id), newDataItem]);
        setEditItem(null);
    };

    const edit = (dataItem) => {
        dataItem.inEdit = true;
        setEditItem(dataItem);

        const newData = data.map((item) =>
            item.id === dataItem.id
                ? { ...item, ...dataItem }
                : { ...item }
        );
        setData(newData);
    };

    const update = (dataItem) => {
        if (!isDataItemValid(dataItem)) {
            return;
        }

        dataItem.fromValue = dataItem.fromValue !== '' ? dataItem.fromValue : undefined;
        dataItem.toValue = dataItem.toValue !== '' ? dataItem.toValue : undefined;
        dataItem.inEdit = false;

        const newData = data.map((item) =>
            item.id === dataItem.id
                ? { ...item, ...dataItem }
                : { ...item }
        );
        setData(newData);
        setEditItem(null);
    };

    const remove = (dataItem) => {
        setIsConfirmDeleteVisible(true);
        setDeleteItem(dataItem);
        setDisable(false);
    };

    const discard = () => {
        const newData = data.filter((x) => x.id !== 0);
        setData(newData);
        setEditItem(null);
    };

    const cancel = (dataItem) => {
        const originalItem = filters.find((item) => item.id === dataItem.id) ?? editItem;
        const newData = data.map((item) =>
            item.id === originalItem.id
                ? { ...item, ...originalItem, inEdit: false }
                : item
        );
        setData(newData);
        setEditItem(null);
    };

    const onConfirmDelete = () => {
        const newData = data.filter((x) => x.id !== deleteItem.id);
        setData(newData);
        setDeleteItem(null);
    };

    const handleConfirmDeleteClose = (isConfirmed) => {
        setIsConfirmDeleteVisible(false);

        if (isConfirmed) {
            onConfirmDelete();
        }
    };

    const isDataItemValid = (dataItem) => {
        removeNotification();

        if (!dataItem.field) {
            dispatchNotification({
                type: NotificationTypes.error,
                pageId: pageId,
                message: <FormattedMessage id="CompleteCountFilters.FieldRequired" />
            });

            return false;
        }

        if (!dataItem.fromValue && !dataItem.toValue) {
            dispatchNotification({
                type: NotificationTypes.error,
                pageId: pageId,
                message: <FormattedMessage id="CompleteCountFilters.ValueFromOrValueToRequired" />
            });

            return false;
        }

        if (dataItem.fromValue && dataItem.toValue) {
            const floatFromValue = parseFloat(dataItem.fromValue);
            const floatToValue = parseFloat(dataItem.toValue);
            const fromValue = isNaN(floatFromValue) ? dataItem.fromValue : floatFromValue;
            const toValue = isNaN(floatToValue) ? dataItem.toValue : floatToValue;

            if (fromValue > toValue) {
                dispatchNotification({
                    type: NotificationTypes.error,
                    pageId: pageId,
                    message: <FormattedMessage id="CompleteCountFilters.ValueFromLowerThanValueTo" />
                });

                return false;
            }
        }

        return true;
    };

    const removeNotification = () => {
        dispatchNotification({
            remove: true,
            pageId: pageId
        });
    };

    const ActionCell = (props) => (
        <CompleteCountFilterActionCell
            {...props}
            add={add}
            edit={edit}
            update={update}
            remove={remove}
            discard={discard}
            cancel={cancel}
            editField={editField}
            idField={idField}
            disabled={disable || readOnly}
        />
    );

    return (
        <>
            <Card>
                <CardHeader>
                    <h3>
                        <div className="float-left">
                            <Button
                                color="primary"
                                disabled={readOnly}
                                onClick={onReloadStratums}
                            >
                                <FontAwesomeIcon icon={faArrowsRotate} />
                            </Button>

                            <Button
                                color="primary"
                                disabled={readOnly}
                                onClick={handleAddFilter}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </div>

                        <div className="float-right">
                            <Button
                                color="secondary"
                                disabled={!isAnyChange}
                                onClick={handleReset}
                            >
                                <FormattedMessage id='Control.Reset' />
                            </Button>

                            <Button
                                color="primary"
                                disabled={!isAnyChange}
                                onClick={handleSave}
                            >
                                <FormattedMessage id='Control.Save' />
                            </Button>
                        </div>
                    </h3>
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col xs="12">
                            <Grid
                                data={process(data.map((item) => ({
                                    ...item,
                                    dataSourceFields: fields,
                                })), dataState)}
                                {...dataState}
                                onDataStateChange={(e) => {
                                    setDataState(e.dataState);
                                }}
                                onItemChange={itemChange}
                                editField={editField}
                            >
                                <GridNoRecords>
                                    <FormattedMessage id="CompleteCountFilters.NoData" />
                                </GridNoRecords>
                                <Column field="field" title={intl.formatMessage({ id: "CompleteCountFilters.FieldForCompleteCount" })} cell={FieldsDropDown} />
                                <Column field="fromValue" title={intl.formatMessage({ id: "CompleteCountFilters.ValueFrom" })} />
                                <Column field="toValue" title={intl.formatMessage({ id: "CompleteCountFilters.ValueTo" })} />
                                <Column cell={ActionCell} width="120px" />
                            </Grid>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <ConfirmDialog
                visible={isConfirmDeleteVisible}
                onClose={handleConfirmDeleteClose}
                negative={<FormattedMessage id='Control.Cancel' />}
                positive={<FormattedMessage id='Control.Delete' />}
                title={<FormattedMessage id='CompleteCountFilters.DeleteConfirmationTitle' />}
                detail={<FormattedMessage id='CompleteCountFilters.DeleteConfirmationDetail' />}
            />
        </>
    );
};

CompleteCountFilters.propTypes = {
    filters: [PropTypes.object],
    filterFields: [PropTypes.object],
    onSave: PropTypes.func,
    onIsAnyChangeChanged: PropTypes.func,
    onReloadStratums: PropTypes.func,
    readOnly: PropTypes.bool,
};

export default CompleteCountFilters;
